import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Trash2, Download, Cookie, UserCheck, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import BackToHomeLink from '../components/BackToHomeLink';

const Section = ({ icon: Icon, title, children, delay = 0 }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <motion.section
      className="mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white/50 rounded-xl hover:bg-white/80 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[#a5400e]/10 flex items-center justify-center">
            <Icon className="text-[#a5400e]" size={20} />
          </div>
          <h2 className="text-lg font-semibold text-[#5c2810]">{title}</h2>
        </div>
        {isOpen ? <ChevronUp className="text-[#7a4a3a]" size={20} /> : <ChevronDown className="text-[#7a4a3a]" size={20} />}
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="p-4 pt-6 text-[#7a4a3a] leading-relaxed"
        >
          {children}
        </motion.div>
      )}
    </motion.section>
  );
};

const PolitiqueConfidentialite = () => {
  const currentDate = new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F0E8] to-[#F8F4ED] pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a5400e]/10 mb-4">
            <Shield className="text-[#a5400e]" size={18} />
            <span className="text-sm text-[#a5400e] font-medium">Protection des données</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif text-[#5c2810] mb-4">
            Politique de Confidentialité
          </h1>
          <p className="text-[#7a4a3a]">
            Dernière mise à jour : {currentDate}
          </p>
        </motion.div>

        {/* Banner RGPD */}
        <motion.div
          className="bg-gradient-to-r from-[#a5400e] to-[#d46a3e] rounded-2xl p-6 sm:p-8 mb-8 text-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <UserCheck className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-2">Vos données sont protégées</h2>
              <p className="text-white/90">
                Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés,
                nous nous engageons à protéger vos données personnelles et à respecter votre vie privée.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 md:p-8 border border-[#a5400e]/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Section icon={Eye} title="1. Qui sommes-nous ?" delay={0.2}>
            <p className="mb-4">
              Le site <strong>s.belliard-reynes.fr</strong> est exploité par <strong>Siriane Belliard-Reynes</strong>,
              neuro-thérapeute et praticienne en kinésiologie, située au 9 rue du Docteur Mourier, 77430 Champagne-sur-Seine.
            </p>
            <p>
              <strong>Responsable du traitement des données :</strong><br />
              Siriane Belliard-Reynes<br />
              Email : neuro.endo.therapeute@gmail.com<br />
              Téléphone : 06 36 14 31 26
            </p>
          </Section>

          <Section icon={Lock} title="2. Quelles données collectons-nous ?" delay={0.3}>
            <p className="mb-4">Nous collectons uniquement les données nécessaires à la gestion de nos services :</p>
            <div className="bg-[#F5F0E8] p-4 rounded-xl space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#a5400e] mt-2" />
                <div>
                  <strong>Données d'identification</strong> : nom, prénom
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#a5400e] mt-2" />
                <div>
                  <strong>Coordonnées</strong> : adresse email, numéro de téléphone
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#a5400e] mt-2" />
                <div>
                  <strong>Messages</strong> : contenu des demandes de contact
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm bg-[#a5400e]/10 p-4 rounded-lg border-l-4 border-[#a5400e]">
              <AlertCircle className="inline-block mr-2 text-[#a5400e]" size={16} />
              <strong>Important :</strong> {"Le formulaire sert uniquement à la prise de contact. Merci de ne pas y transmettre de données de santé, de diagnostic ou de document médical."}
            </p>
          </Section>

          <Section icon={Shield} title="3. Pourquoi collectons-nous vos données ?" delay={0.4}>
            <p className="mb-4">Nous traitons vos données personnelles pour les finalités suivantes :</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-[#F5F0E8] p-4 rounded-xl">
                <h4 className="font-semibold text-[#5c2810] mb-2">Gestion des rendez-vous</h4>
                <p className="text-sm">Prise de rendez-vous, confirmations, rappels de consultation</p>
              </div>
              <div className="bg-[#F5F0E8] p-4 rounded-xl">
                <h4 className="font-semibold text-[#5c2810] mb-2">Réponse aux demandes</h4>
                <p className="text-sm">Traitement des messages et questions envoyés via le formulaire</p>
              </div>
              <div className="bg-[#F5F0E8] p-4 rounded-xl">
                <h4 className="font-semibold text-[#5c2810] mb-2">Obligations légales</h4>
                <p className="text-sm">Conservation des dossiers conformément à la réglementation</p>
              </div>
              <div className="bg-[#F5F0E8] p-4 rounded-xl">
                <h4 className="font-semibold text-[#5c2810] mb-2">Amélioration du service</h4>
                <p className="text-sm">Statistiques anonymisées pour améliorer l'expérience utilisateur</p>
              </div>
            </div>
            <p className="mt-4"><strong>Bases légales :</strong> exécution du contrat, consentement, intérêt légitime, obligations légales.</p>
          </Section>

          <Section icon={Lock} title="4. Comment protégeons-nous vos données ?" delay={0.5}>
            <p className="mb-4">Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles :</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-[#a5400e]">✓</span>
                <span>Chiffrement des données en transit (HTTPS/TLS)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#a5400e]">✓</span>
                <span>Hébergement sécurisé certifié ISO 27001</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#a5400e]">✓</span>
                <span>Accès restreint aux données personnelles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#a5400e]">✓</span>
                <span>Sauvegardes régulières et sécurisées</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#a5400e]">✓</span>
                <span>Formation et sensibilisation à la protection des données</span>
              </li>
            </ul>
          </Section>

          <Section icon={Trash2} title="5. Combien de temps conservons-nous vos données ?" delay={0.6}>
            <p className="mb-4">Nous conservons vos données uniquement le temps nécessaire :</p>
            <div className="bg-[#F5F0E8] p-4 rounded-xl space-y-2">
              <p><strong>Données de contact</strong> : 3 ans après le dernier contact</p>
              <p><strong>Données de rendez-vous</strong> : Durée légale applicable (5 ans pour les dossiers médicaux)</p>
              <p><strong>Cookies techniques</strong> : 13 mois maximum</p>
            </div>
            <p className="mt-4 text-sm">
              Passé ce délai, vos données sont supprimées ou anonymisées de manière sécurisée.
            </p>
          </Section>

          <Section icon={UserCheck} title="6. Vos droits (RGPD)" delay={0.7}>
            <p className="mb-4">Conformément au RGPD, vous disposez des droits suivants :</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { title: "Droit d'accès", desc: "Obtenir une copie de vos données" },
                { title: "Droit de rectification", desc: "Modifier vos données inexactes" },
                { title: "Droit à l'effacement", desc: "Demander la suppression de vos données" },
                { title: "Droit à la limitation", desc: "Restreindre le traitement" },
                { title: "Droit à la portabilité", desc: "Récupérer vos données dans un format structuré" },
                { title: "Droit d'opposition", desc: "Vous opposer au traitement de vos données" },
              ].map((right, index) => (
                <div key={index} className="bg-[#F5F0E8] p-3 rounded-lg">
                  <strong className="text-[#5c2810] block">{right.title}</strong>
                  <span className="text-sm text-[#7a4a3a]">{right.desc}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-[#a5400e]/10 p-4 rounded-xl">
              <p className="font-semibold text-[#a5400e] mb-2">Comment exercer vos droits ?</p>
              <p>Contactez-nous par email : <strong>neuro.endo.therapeute@gmail.com</strong></p>
              <p className="text-sm mt-2">Nous vous répondrons dans un délai maximum d'un mois.</p>
            </div>
            <p className="mt-4 text-sm">
              Vous avez également le droit d'introduire une réclamation auprès de la
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#a5400e] hover:underline"> CNIL</a>.
            </p>
          </Section>

          <Section icon={Cookie} title="7. Cookies et technologies similaires" delay={0.8}>
            <p className="mb-4">Notre site utilise uniquement des cookies strictement nécessaires :</p>
            <div className="bg-[#F5F0E8] p-4 rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#a5400e]/20">
                    <th className="text-left py-2 text-[#5c2810]">Type</th>
                    <th className="text-left py-2 text-[#5c2810]">Finalité</th>
                    <th className="text-left py-2 text-[#5c2810]">Durée</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2">Techniques</td>
                    <td className="py-2">Fonctionnement du site</td>
                    <td className="py-2">Session / 13 mois</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm bg-gray-100 p-4 rounded-lg">
              <strong>Note :</strong> Nous n'utilisons pas de cookies publicitaires, de tracking ou d'analyse comportementale.
              Votre navigation n'est pas suivie à des fins marketing.
            </p>
          </Section>

          <Section icon={Download} title="8. Transfert des données" delay={0.9}>
            <p className="mb-4">
              Vos données sont hébergées en France par IONOS SE et ne sont pas transférées en dehors de l'Union Européenne.
            </p>
            <p>
              Dans le cas où un transfert serait nécessaire, nous nous assurerons que des garanties appropriées sont en place
              (pays bénéficiant d'une décision d'adéquation de la Commission Européenne ou clauses contractuelles types).
            </p>
          </Section>

          <Section icon={Shield} title="9. Modifications de la politique" delay={1.0}>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment.
              Toute modification sera publiée sur cette page avec mise à jour de la date de dernière modification.
              Nous vous encourageons à consulter régulièrement cette page.
            </p>
          </Section>

          <Section icon={UserCheck} title="10. Contact DPO" delay={1.1}>
            <p className="mb-4">
              Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits,
              contactez-nous :
            </p>
            <div className="bg-gradient-to-r from-[#a5400e]/10 to-[#d46a3e]/10 p-6 rounded-xl border border-[#a5400e]/20">
              <p><strong>Siriane Belliard-Reynes</strong></p>
              <p>Email : neuro.endo.therapeute@gmail.com</p>
              <p>Téléphone : 06 36 14 31 26</p>
              <p>Adresse : 9 rue du Docteur Mourier, 77430 Champagne-sur-Seine</p>
            </div>
          </Section>
        </motion.div>

        {/* Back to home */}
        <motion.div
          className="mt-12 flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.5 }}
        >
          <BackToHomeLink variant="solid" />
        </motion.div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;
